import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { PayButtons } from "@/components/PayButtons";
import { RedeemGiftCard } from "@/components/gift/RedeemGiftCard";
import { RefundAgree } from "@/components/RefundAgree";
import { Button } from "@/components/ui/button";
import { PRICING } from "@/data/categories";
import { LifetimeBanner } from "@/components/LifetimeBanner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { checkPrivateSupport } from "@/lib/ebookLocal";
import { toast } from "sonner";
import { Gift,
  BookOpen, Check, Infinity as InfinityIcon, ShieldCheck,
  Sparkle, DeviceMobile, Wallet,
} from "@phosphor-icons/react";

const LIFETIME_BENEFITS = [
  "One payment unlocks ALL life seasons at once (Childhood \u2192 Seniors)",
  "Record unlimited memories and dreams",
  "View, edit and download your own memories \u2014 always, no extra fee",
  "Your data is never deleted",
];

export default function Upgrade() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [payingKey, setPayingKey] = useState(null);
  const [paypalKey, setPaypalKey] = useState(null);
  const [refundOk, setRefundOk] = useState(false);
  const [photoSupport] = useState(checkPrivateSupport);

  useEffect(() => {
    api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => setCfg({ client_id: "" }));
  }, []);

  const startGcash = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!cfg?.paymongo_enabled) {
      toast("GCash is coming soon for Philippine customers. Card and PayPal are available now.");
      return;
    }
    if (!refundOk) { toast.error("Please tick the Refund Policy box first."); return; }
    setPayingKey("gcash");
    try {
      const r = await api.post("/payments/paymongo/checkout", { origin_url: window.location.origin, refund_policy_accepted: refundOk });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      setPayingKey(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start GCash checkout. Please try again.");
    }
  };

  const onPaid = (r) => {
    if (r.payment_status !== "paid") {
      toast("You already owned this, so the duplicate payment is being refunded.");
      return;
    }
    if (r.lookup_key === "lifetime_access") {
      toast.success("Welcome to Lifetime Access — every season is now yours.");
      navigate("/timeline");
    } else {
      toast.success("Your keepsake is unlocked — create it any time from Keepsakes.");
      navigate(r.lookup_key === "life_story_ebook" ? "/ebook-studio" : "/timeline?view=keepsakes");
    }
  };

  const body = (
    <div className="mx-auto max-w-3xl px-6 py-10">

      <div className="mb-8 flex items-center gap-2">
        <BookOpen size={26} weight="duotone" className="text-[#B08D57]" />
        <span className="font-serif text-xl text-[#2C2A28]">Your Life Journeys Matter</span>
      </div>

      <p className="font-handwriting text-3xl text-[#B08D57]">one payment, forever</p>
      <h1 className="mt-1 font-serif text-4xl leading-tight tracking-tight text-[#2C2A28] sm:text-5xl">
        Unlock your whole life journey
      </h1>
      <p data-testid="upgrade-gst-note" className="mt-2 text-xs font-semibold text-[#8A7F76]">All prices in Australian dollars (AUD) · No GST applies</p>

      {!user?.is_pro && (
        <div className="mt-6">
          <LifetimeBanner onCta={() => document.getElementById("upgrade-pay")?.scrollIntoView({ behavior: "smooth", block: "start" })} />
        </div>
      )}

      {/* Clear one-time messaging */}
      <div
        data-testid="onetime-callout"
        className="mt-6 flex items-start gap-3 rounded-xl border border-[#7EA193]/40 bg-[#EAF1EE] p-4"
      >
        <ShieldCheck size={22} weight="duotone" className="mt-0.5 shrink-0 text-[#7EA193]" />
        <p className="text-[#4A6B5D]">
          This is <strong>one single payment</strong> that unlocks <strong>all life
          seasons</strong> forever. There is <strong>no separate fee for each
          season</strong>, no subscription, and no automatic charges — ever.
        </p>
      </div>

      {/* Lifetime card */}
      <div className="mt-6 rounded-2xl border border-[#B08D57]/30 bg-white p-7 shadow-sm">
        <div className="flex items-center gap-2">
          <InfinityIcon size={24} weight="duotone" className="text-[#B08D57]" />
          <h2 className="font-serif text-2xl text-[#2C2A28]">{PRICING.lifetime.label}</h2>
          {PRICING.lifetime.launch && (
            <span className="rounded-full bg-[#7EA193] px-2 py-0.5 text-[10px] text-white">Launch price</span>
          )}
        </div>
        <div className="mt-3 flex items-end gap-2">
          <span className="font-serif text-5xl text-[#2C2A28]">{PRICING.currency}{PRICING.lifetime.price}</span>
          <span className="mb-1.5 text-sm text-[#8A7F76]">one-time · unlocks everything</span>
        </div>
        <p className="mt-1 text-sm text-[#8A7F76]">{PRICING.lifetime.tagline}</p>
        {PRICING.lifetime.launch && (
          <p className="mt-1 text-xs italic text-[#B08D57]">Introductory launch price — this may rise later.</p>
        )}
        <ul className="mt-5 space-y-2.5">
          {LIFETIME_BENEFITS.map((b) => (
            <li key={b} className="flex items-center gap-2 text-[#4A4540]">
              <Check size={18} weight="bold" className="shrink-0 text-[#7EA193]" /> {b}
            </li>
          ))}
        </ul>

        {/* Payment methods */}
        <div id="upgrade-pay" className="mt-7 scroll-mt-24">
          <p className="mb-1 text-sm font-medium text-[#2C2A28]">Choose how you'd like to pay</p>
          <div className="mb-3 rounded-lg bg-[#F4EFE6] p-3 text-sm text-[#4A4540]" data-testid="payment-instructions">
            You'll be charged <strong>{PRICING.currency}{PRICING.lifetime.price} once — total</strong>. This single payment
            unlocks <strong>every</strong> life season at once. You are <strong>never</strong> billed per
            season, and never charged again.
          </div>
          <div className="space-y-3">
            {!user ? (
              <div className="rounded-lg border border-[#B08D57]/30 bg-[#F7E3DE] p-5 text-center" data-testid="login-required-block">
                <p className="text-sm text-[#7A3D34]">
                  To keep your purchase linked to your private journal, please sign in first.
                  Your payment options will appear here right after you log in.
                </p>
                <Button
                  data-testid="login-to-continue-btn"
                  onClick={() => navigate("/account?next=/upgrade")}
                  className="mt-4 w-full rounded-full bg-[#E07A5A] py-6 text-base font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-[#d66b4b]"
                >
                  Log in to continue
                </Button>
                <p className="mt-2 text-xs text-[#8A7F76]">
                  New here? The same screen lets you create your diary in seconds.
                </p>
              </div>
            ) : (
              <>
                <RefundAgree checked={refundOk} onChange={setRefundOk} testId="refund-agree-lifetime" />
                {user.is_pro ? (
                  <p data-testid="lifetime-owned" className="rounded-lg bg-[#EAF1EE] p-4 text-center text-sm font-medium text-[#4A6B5D]">
                    <Check size={16} weight="bold" className="mr-1 inline" /> You already have Lifetime Access — nothing more to pay.
                  </p>
                ) : (
                  <>
                    <PayButtons payload={{ lookup_key: "lifetime_access" }} refundOk={refundOk} paypalReady={!!cfg?.client_id} paypalMode={cfg?.mode} cardEnabled={cfg?.card_enabled !== false}
                      onPaid={onPaid} testPrefix="lifetime" />
                    <p className="-mt-1 text-center text-xs text-[#8A7F76]">Visa · Mastercard · Amex — secured by Stripe. No account needed.</p>
                  </>
                )}

                {/* GCash — Philippines (PayMongo) */}
                <button
                  data-testid="pay-gcash-btn"
                  onClick={startGcash}
                  disabled={!refundOk || payingKey === "gcash"}
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-[#4A4540]/25 bg-white py-4 text-[#8A7F76] transition-colors hover:border-[#B08D57] disabled:opacity-60"
                >
                  <DeviceMobile size={20} weight="duotone" />
                  {payingKey === "gcash" ? "Opening GCash…" : "Pay with GCash"}
                  <span className="rounded-full bg-[#F4EFE6] px-2 py-0.5 text-[10px] text-[#8A5A2B]">
                    {cfg?.paymongo_enabled ? "PH · ₱999" : "PH · soon"}
                  </span>
                </button>
              </>
            )}
          </div>
          <p className="mt-3 text-center text-xs text-[#8A7F76]">
            No subscription · No renewal · No auto-charge
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2 [&>*]:min-w-0">
        <div data-testid="gift-invite-card" className="rounded-2xl bg-[#FFF4F0] p-5 sm:p-6">
          <p className="flex items-center gap-2 font-serif text-xl text-[#A84A32]"><Gift size={20} weight="duotone" /> Give Lifetime Access as a gift</p>
          <p className="mt-1 text-sm text-[#4A4540]">{PRICING.currency}{PRICING.lifetime.price} · includes a printable gift certificate and a code valid for 3 years.</p>
          <Button data-testid="gift-invite-btn" onClick={() => navigate(user ? "/gift" : "/account?next=/gift")}
            className="mt-4 min-h-[44px] rounded-full bg-[#E06D53] px-6 text-white hover:bg-[#C85B43]">Choose a gift</Button>
        </div>
        {user && !user.is_pro && <RedeemGiftCard onDone={() => navigate("/timeline?celebrate=1")} />}
      </div>

      {/* Optional AI add-ons — pricing under review */}
      <div className="mt-8" data-testid="ai-keepsakes-section">
        <div className="mb-2 flex items-center gap-2">
          <Sparkle size={18} weight="duotone" className="text-[#B08D57]" />
          <h3 className="font-serif text-xl text-[#2C2A28]">Optional AI keepsakes</h3>
        </div>
        <p className="mb-3 text-sm text-[#8A7F76]">
          Separate, optional keepsakes — polished, AI-crafted from your own memories.
          You only pay for the one you choose. Never automatic.
        </p>

        {user && (
          <div className="mb-3">
            <RefundAgree checked={refundOk} onChange={setRefundOk} testId="refund-agree-keepsakes" />
          </div>
        )}
        <div className="space-y-2">
          {PRICING.ai.map((item) => {
            const owned = user?.entitlements?.includes(item.key);
            const isEbook = item.key === "life_story_ebook";
            return (
              <div key={item.id} data-testid={`keepsake-card-${item.key}`} className="rounded-xl border border-[#4A4540]/10 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[#2C2A28]">{item.label}</p>
                    {item.coverage && <p className="text-xs font-medium text-[#A84A32]">{item.coverage}</p>}
                    <p className="mt-0.5 text-xs text-[#8A7F76]">{item.desc}</p>
                    {item.note && <p className="mt-1 text-[11px] italic text-[#8A7F76]">{item.note}</p>}
                  </div>
                  <span className="shrink-0 font-serif text-lg text-[#2C2A28]">{PRICING.currency}{item.price}</span>
                </div>

                {isEbook && (
                  <p data-testid="ebook-compat-check" className={`mt-2 text-xs font-medium ${photoSupport ? "text-[#2E6B4F]" : "text-[#8A5A2B]"}`}>
                    {photoSupport
                      ? "✓ This device can add photos to your Ebook privately (photos never leave your device)."
                      : "This device can't add photos privately — your Ebook can still be created without photos, or use another device for photos."}
                  </p>
                )}
                {item.perMemory || item.key === "yearly_reflection" ? (
                  <Button
                    data-testid={`keepsake-choose-${item.perMemory ? "memory" : "year"}-${item.key}`}
                    onClick={() => navigate(user ? (item.perMemory ? "/timeline" : "/timeline?view=keepsakes") : "/account")}
                    className="mt-3 w-full rounded-full bg-[#A84A32] py-3 text-sm font-semibold text-white hover:bg-[#8f3e29]"
                  >
                    {item.perMemory ? "Open a memory to get its Memory Insight" : "Choose your year in Keepsakes"}
                  </Button>
                ) : owned ? (
                  <button
                    data-testid={`keepsake-owned-${item.key}`}
                    onClick={() => navigate(isEbook ? "/ebook-studio" : "/timeline?view=keepsakes")}
                    className="mt-3 inline-flex items-center gap-1 rounded-full bg-[#EAF1EE] px-3 py-1.5 text-xs font-medium text-[#4A6B5D] hover:bg-[#dcebe4]"
                  >
                    <Check size={14} weight="bold" /> Owned — create it now in Keepsakes →
                  </button>
                ) : !user ? (
                  <Button
                    data-testid={`keepsake-login-${item.key}`}
                    onClick={() => navigate("/account")}
                    className="mt-3 w-full rounded-full bg-[#E07A5A] py-3 text-sm font-semibold text-white hover:bg-[#d66b4b]"
                  >
                    Log in to buy
                  </Button>
                ) : (
                  <div className="mt-3 space-y-2">
                    <PayButtons payload={{ lookup_key: item.key }} refundOk={refundOk}
                      paypalReady={!!cfg?.client_id && paypalKey === item.key} paypalMode={cfg?.mode} cardEnabled={cfg?.card_enabled !== false} onPaid={onPaid}
                      cardLabel={`Pay with Card · ${PRICING.currency}${item.price}`} testPrefix={`keepsake-${item.key}`} />
                    {cfg?.client_id && paypalKey !== item.key && (
                      <button
                        data-testid={`buy-keepsake-paypal-${item.key}`}
                        onClick={() => setPaypalKey(item.key)}
                        disabled={!refundOk}
                        className="w-full rounded-full bg-[#ffc439] py-3 text-sm font-semibold text-[#003087] hover:bg-[#f5bb2e] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Wallet size={16} weight="duotone" className="mr-2 inline" /> Pay with PayPal
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Legacy Edition — coming later */}
          <div data-testid="keepsake-card-legacy" className="rounded-xl border border-dashed border-[#4A4540]/20 bg-[#FAF6EE] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#2C2A28]">{PRICING.legacy.label}</p>
                <p className="mt-0.5 text-xs text-[#8A7F76]">{PRICING.legacy.desc}</p>
              </div>
              <span className="shrink-0 rounded-full bg-[#F4EFE6] px-2 py-0.5 text-[11px] text-[#8A5A2B]">Coming later</span>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate(user ? "/ebook-studio" : "/account?next=/ebook-studio")}
          data-testid="view-ebook-demo-btn"
          className="mt-4 text-sm text-[#B08D57] underline-offset-2 hover:underline"
        >
          Preview the private Ebook photo studio →
        </button>
      </div>

      <button
        onClick={() => navigate("/terms")}
        className="mt-8 block text-sm text-[#B08D57] underline-offset-2 hover:underline"
      >
        Read our simple terms →
      </button>
      <a href="/refunds" data-testid="upgrade-refund-link" className="mt-2 block text-sm text-[#B08D57] underline-offset-2 hover:underline">
        Read our Refund Policy →
      </a>
    </div>
  );

  if (cfg?.client_id) {
    return (
      <PayPalScriptProvider
        options={{ clientId: cfg.client_id, currency: cfg.currency || "AUD", intent: "capture", components: "buttons", disableFunding: "card" }}
      >
        {body}
      </PayPalScriptProvider>
    );
  }
  return body;
}
